import { useState } from 'react';
import Modal from '../modal/modal';
import Card from '../card/Card';
import P from '../texts/p/P';
import Button from '../button/Button';
import Input from '../input/Input';
import CheckBox from '../checkBox/CheckBox';
import { OverdueData } from '../../utils/overdueColumns';
import { updateGuaranteeStatus, updateLoanRate, createPartialPayment } from '../../services/GuaranteesService';
import { useToast } from '../toast/toast';

interface OverdueStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  overdueItem: OverdueData | null;
  onSuccess: () => void;
}

const OverdueStatusModal: React.FC<OverdueStatusModalProps> = ({
  isOpen,
  onClose,
  overdueItem,
  onSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [ratePercentage, setRatePercentage] = useState('');
  const [honrarMontoCompleto, setHonrarMontoCompleto] = useState(true);
  const [montoHonrado, setMontoHonrado] = useState('');

  const handleUpdateStatus = async (status: number) => {
    if (!overdueItem) return;

    setIsLoading(true);
    try {
      await updateGuaranteeStatus(
        parseInt(overdueItem.id_global),
        overdueItem.installment_number,
        status
      );
      useToast.success({
        title: status === 2
          ? 'Cuota marcada como afrontada exitosamente'
          : 'Cuota marcada como honrada exitosamente',
        id: 'overdue-toast',
        autoClose: 5000,
      });
      onSuccess();
      onClose();
    } catch (error) {
      useToast.error({
        title: 'Error al actualizar el estado de la cuota',
        id: 'overdue-toast',
        autoClose: 5000,
      });
      console.error('Error updating guarantee status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateRate = async () => {
    if (!overdueItem || !ratePercentage) {
      useToast.error({
        title: 'Ingrese un porcentaje válido',
        id: 'overdue-toast',
        autoClose: 5000,
      });
      return;
    }

    setIsLoading(true);
    try {
      await updateLoanRate(
        overdueItem.id,
        overdueItem.installment_number,
        parseFloat(ratePercentage)
      );
      useToast.success({
        title: 'Tasa actualizada exitosamente',
        id: 'overdue-toast',
        autoClose: 5000,
      });
      onSuccess();
      onClose();
    } catch (error) {
      useToast.error({
        title: 'Error al actualizar la tasa',
        id: 'overdue-toast',
        autoClose: 5000,
      });
      console.error('Error updating loan rate:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStatusWithAmount = async (status: number) => {
    if (!overdueItem) return;

    const statusType = overdueItem.status_type;
    const isAfrontada = statusType === 'Afrontada' || statusType === 'Afrontada con pago parcial';
    const isVencida = statusType === 'Vencida';

    if (!honrarMontoCompleto) {
      const monto = parseFloat(montoHonrado);
      if (!montoHonrado || isNaN(monto) || monto <= 0) {
        useToast.error({
          title: 'Ingrese un monto válido',
          id: 'overdue-toast',
          autoClose: 5000,
        });
        return;
      }
      
      // Para Afrontada o Afrontada con pago parcial, validar contra amount - partial_payment_amount
      if (isAfrontada) {
        const partialPayment = overdueItem.partial_payment_amount || 0;
        const maxAmount = (overdueItem.amount || overdueItem.monetization_amount) - partialPayment;
        if (monto > maxAmount) {
          useToast.error({
            title: `El monto no puede superar ${maxAmount.toLocaleString('es-AR')}`,
            id: 'overdue-toast',
            autoClose: 5000,
          });
          return;
        }
      } else if (monto > overdueItem.monetization_amount) {
        useToast.error({
          title: 'El monto no puede superar el monto de monetización',
          id: 'overdue-toast',
          autoClose: 5000,
        });
        return;
      }
    }

    setIsLoading(true);
    try {
      const usePartialPayments = 
        (isAfrontada && status === 3) ||
        (isVencida && status === 3 && !honrarMontoCompleto);

      if (usePartialPayments) {
        const amount = honrarMontoCompleto 
          ? overdueItem.monetization_amount 
          : parseFloat(montoHonrado);
        
        await createPartialPayment(
          overdueItem.id,
          new Date().toISOString().split('T')[0],
          honrarMontoCompleto,
          amount,
          overdueItem.installment_number
        );
        useToast.success({
          title: 'Cuota marcada como honrada exitosamente',
          id: 'overdue-toast',
          autoClose: 5000,
        });
      } else {
        await updateGuaranteeStatus(
          parseInt(overdueItem.id_global),
          overdueItem.installment_number,
          status
        );
        useToast.success({
          title: status === 2
            ? 'Cuota marcada como afrontada exitosamente'
            : 'Cuota marcada como honrada exitosamente',
          id: 'overdue-toast',
          autoClose: 5000,
        });
      }
      onSuccess();
      onClose();
    } catch (error) {
      useToast.error({
        title: 'Error al actualizar el estado de la cuota',
        id: 'overdue-toast',
        autoClose: 5000,
      });
      console.error('Error updating guarantee status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckboxChange = (checked: boolean) => {
    setHonrarMontoCompleto(checked);
    if (checked && overdueItem) {
      setMontoHonrado(overdueItem.monetization_amount.toString());
    } else {
      setMontoHonrado('');
    }
  };

  const renderButtons = () => {
    const statusType = overdueItem?.status_type;

    // Estado: Honrada - Botón disabled
    if (statusType === 'Honrada') {
      return (
        <Button variant='success' disabled fullWidth>
          Ya fue honrado
        </Button>
      );
    }

    // Estado: Pendiente - Solo botón "Marcar como honrada"
    if (statusType === 'Pendiente') {
      return (
        <Button
          variant='success'
          onClick={() => handleUpdateStatus(3)}
          disabled={isLoading}
          fullWidth
        >
          Marcar como honrada
        </Button>
      );
    }

    // Estado: Afrontada o Afrontada con pago parcial
    if (statusType === 'Afrontada' || statusType === 'Afrontada con pago parcial') {
      return (
        <Card display='grid' gap='16px'>
          <Card display='flex' gap='16px' alignItems='center'>
            <Button
              variant='success'
              onClick={() => handleUpdateStatusWithAmount(3)}
              disabled={isLoading}
              style={{ flex: 1 }}
            >
              Marcar como honrada
            </Button>
            <CheckBox
              checked={honrarMontoCompleto}
              onChange={handleCheckboxChange}
              disabled={isLoading}
            >
              Honrar monto completo
            </CheckBox>
          </Card>
          <Input
            type='number'
            placeholder='Ingrese el monto'
            value={montoHonrado}
            onChange={(e) => setMontoHonrado(e.target.value)}
            disabled={isLoading || honrarMontoCompleto}
            readOnly={honrarMontoCompleto}
          />
        </Card>
      );
    }

    // Estado: Vencida
    if (statusType === 'Vencida') {
      return (
        <Card display='grid' gap='16px'>
          <Button
            variant='success'
            onClick={() => handleUpdateStatus(2)}
            disabled={isLoading}
            fullWidth
          >
            Marcar como afrontada
          </Button>
          <Button
            variant='success'
            onClick={() => handleUpdateStatus(3)}
            disabled={isLoading}
            fullWidth
          >
            Marcar como honrada
          </Button>
        </Card>
      );
    }

    // Fallback por si no hay status_type o no coincide
    return null;
  };

  if (!overdueItem) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title='Modificar estado de cuota'
      width='800px'
      primaryText='Cancelar'
      primaryVariant='contained'
      onPrimaryAction={onClose}
      secondaryText=''
      secondaryVariant='tertiary'
      onSecondaryAction={onClose}
      showOverlay={true}
    >
      <Card margin={{ top: '4' }}>
        <Card display='grid' gap='16px'>
          {/* Primera fila */}
          <Card display='flex' justifyContent='space-between' gap='24px'>
            <Card display='flex' gap='8px' width='38%'>
              <P type='3' variant='neutral' weight='bold' withMargin={false}>
                CUIT:
              </P>
              <P type='3' variant='neutral' withMargin={false}>
                {overdueItem.cuit}
              </P>
            </Card>
            <Card display='flex' gap='8px' width='58%'>
              <P type='3' variant='neutral' weight='bold' withMargin={false}>
                Razón social:
              </P>
              <P type='3' variant='neutral' withMargin={false}>
                {overdueItem.company_name}
              </P>
            </Card>
          </Card>

          {/* Segunda fila */}
          <Card display='flex' justifyContent='space-between' gap='24px'>
            <Card display='flex' gap='8px' width='38%'>
              <P type='3' variant='neutral' weight='bold' withMargin={false}>
                Nº cuota:
              </P>
              <P type='3' variant='neutral' withMargin={false}>
                {overdueItem.installment_number && overdueItem.total_installments
                  ? `${overdueItem.installment_number} de ${overdueItem.total_installments}`
                  : '-'}
              </P>
            </Card>
            <Card display='flex' gap='8px' width='58%'>
              <P type='3' variant='neutral' weight='bold' withMargin={false}>
                Monto:
              </P>
              <P type='3' variant='neutral' withMargin={false}>
                {overdueItem.monetization_amount.toLocaleString('es-AR')}
              </P>
            </Card>
          </Card>
        </Card>

        {/* Botones */}
        <Card display='grid' gap='16px' margin={{ top: '5' }} justifyContent='center'>
          {/* Input para actualizar tasa (solo para Prestamo con rate_type_id = 2) */}
          {overdueItem.type === 'PRESTAMO' && overdueItem.rate_type_id === 2 && (
            <Card display='flex' gap='16px' margin={{ top: '4' }} alignItems='center'>
              <Input
                type='number'
                placeholder={overdueItem.rate_percentage?.toString() || ''}
                value={ratePercentage}
                onChange={(e) => setRatePercentage(e.target.value)}
                disabled={isLoading}
              />
              <Button
                variant='success'
                onClick={handleUpdateRate}
                disabled={isLoading || !ratePercentage}
              >
                Guardar
              </Button>
            </Card>
          )}
          {renderButtons()}
        </Card>
      </Card>
    </Modal>
  );
};

export default OverdueStatusModal;
